import { ForgotPasswordDto } from 'libs/end-to-end/src/auth';
import { GenericSuccess } from 'libs/end-to-end/src/generic';
import { deserializeError } from 'libs/end-to-end/src/api-error';
import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { AuthLayout } from 'apps/sigma2-ui/src/components/AuthLayout';
import { LegacyButton } from 'apps/sigma2-ui/src/components/ui/LegacyButton';
import { LegacyButtonVariant } from 'apps/sigma2-ui/src/components/ui/Buttons';
import { TextInput } from 'apps/sigma2-ui/src/components/ui/TextInput';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { apiCall } from 'apps/sigma2-ui/src/lib/api';
import { documentTitle } from 'apps/sigma2-ui/src/lib/document-head';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

export const ForgotPasswordPage = () => {
  const language = useLanguage();

  const [email, setEmail] = useState<string>('');
  const [isSubmissionPending, setIsSubmissionPending] =
    useState<boolean>(false);
  const isSubmitDisabled = useMemo(() => {
    if (isSubmissionPending || !email || email.length === 0) {
      return true;
    }

    return false;
  }, [isSubmissionPending, email]);

  const [showSuccess, setShowSuccess] = useState(false);

  const submit = useAsyncCallback(
    async (isAlive) => {
      try {
        if (!isAlive() || !email || email.length === 0) {
          return;
        }

        setIsSubmissionPending(true);

        const request: ForgotPasswordDto = {
          email,
        };

        await apiCall({
          responseSchema: GenericSuccess,
          deserializeErrorFn: deserializeError,
          url: `${getApiBaseUrl()}/auth/forgot-password`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
          },
        });

        if (!isAlive()) {
          return;
        }

        setShowSuccess(true);
      } finally {
        setIsSubmissionPending(false);
      }
    },
    [email],
  );

  return (
    <AuthLayout>
      <Helmet>
        <title>{documentTitle(language.forgotYourPassword())}</title>
      </Helmet>
      <h1
        className="-translate-y-full absolute font-normal pb-5 text-2xl text-gray-100 transform"
        data-qa="forgotPasswordHeader"
      >
        {language.forgotYourPassword()}
      </h1>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="text-left">
          <label htmlFor="email">{language.emailInputLabel()}</label>
          <TextInput
            id="email"
            type="email"
            name="email"
            data-qa="emailInput"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            autoComplete="email"
            autoFocus
            required
          />
          {showSuccess && (
            <p
              data-qa="successMessage"
              className="text-white bg-blue-700 bg-opacity-50 text-sm font-sans inline-block mt-2 px-2 py-1 rounded-sm whitespace-nowrap"
            >
              {language.passwordResetEmailSent()}
            </p>
          )}
        </div>
        <div className="flex justify-end items-start mt-6">
          <LegacyButton
            type="submit"
            variant={LegacyButtonVariant.PRIMARY_DARK}
            disabled={isSubmitDisabled}
          >
            {language.sendPasswordReset()}
          </LegacyButton>
        </div>
      </form>
    </AuthLayout>
  );
};
